import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { useConfigStore } from "@/stores/config";
import { INSPECTION_COMMENTS } from "@/constants/Inspections";
import { getReportFooter, getReportHeader } from "@/reports/ReportUtil";
import { styles } from "@/reports/reportStyles";
import { steelTypeDisplayValue, getMaxMinSpanLength } from "@/composables/spanBySpan";
import { CUSTOM_ICONS, SYMBOLS } from "@/constants/Unicode";
import { useInspectionStore } from "@/stores/inspection";
import { SORT_ORDER, TYPES } from "@/constants/CommonWebConstants";
import { CONFIG_SORT_BY } from "@/constants/EnvConfigProperties";
import { isStructureUnitIn500YrFp } from "@/composables/waterways.js";
import { useCulvertStore } from "@/components/inspection/store/culvert";

function setCulvertDocDefinition(structure) {
  const configStore = useConfigStore();
  const inspectionStore = useInspectionStore();
  const culvertStore = useCulvertStore();
  const structureUnits = culvertStore.getStructureUnits(structure);

  const waterWayUnits = culvertStore.getCulvertWaterwayUnits(structure);

  const pierAndAbutTypes = configStore.getReferenceValues(
    REFERENCE_TABLE.PIER_ABUTMENT_TYPE,
    CONFIG_SORT_BY.VALUE,
    SORT_ORDER.ASC,
    false,
    TYPES.NUMBER,
  );

  const totalNumberOfSpans = culvertStore.getTotalNumberOfSpans(structure);

  let docDefinition = {
    header: function () {
      return getReportHeader("Culvert Page", structure);
    },
    footer: function (currentPage, pageCount) {
      return getReportFooter(currentPage, pageCount);
    },
    pageMargins: [20, 70, 20, 50],
    content: [
      { text: "", pageHeaderText: "Culvert Page" },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Culvert Conditions",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
      },
      {
        columns: [
          {
            width: "40%",
            text: "B.C.04 Culvert Condition Rating: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, structure?.inspection?.culvRating),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        text: "Overall Culvert Notes: ",
        bold: true,
        margin: [0, 3, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find((element) => element?.commentType == INSPECTION_COMMENTS.CULVERT)
          ?.notes,
      },
      {
        columns: [
          {
            width: "40%",
            text: "SUP.01 Combustible Material Under Bridge: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.MATERIAL_STORED_UNDER,
              structure?.userInspection?.matStoreUnd,
            ),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        text: "Overall Combustible Material Under Bridge Notes: ",
        bold: true,
        margin: [0, 3, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.COMBUSTIBLE_MATERIALS_COMMENTS,
        )?.notes,
      },
      {
        columns: [
          {
            width: "40%",
            text: "SUP.02 New Coating Since Last Inspection: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: structure?.userInspection?.newPaint === "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
            style: "customFontAwesome",
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "40%",
            text: "SUP.03 Protective Coating Rating: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.PAINT_CONDITION_RATING,
              structure?.userInspection?.paintCondRate,
            ),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "40%",
            text: "SUP.04 Protective Coating (Extent) Rating: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.PAINT_CONDITION_RATING_EXTENT,
              structure?.userInspection?.paintExtentRate,
            ),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        text: "Overall Protective Coating Notes: ",
        bold: true,
        margin: [0, 3, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.PAINT_OVERALL,
        )?.notes,
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Culvert Details",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
      {
        columns: [
          {
            width: "25%",
            text: "G.01 Total Number of Spans: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text: totalNumberOfSpans,
            margin: [5, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "25%",
            text: "B.G.01 NBIS Bridge Length: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text:
              structure.structureBridge.nbisBridgeLength + " ft",
            margin: [5, 3, 0, 0],
          },
          {
            width: "25%",
            text: "B.G.11 Skew: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text: structure.structureBridge.skew ? structure.structureBridge.skew + SYMBOLS.DEGREE : "",
            margin: [5, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "25%",
            text: "B.G.03 Maximum Span Length: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text:
              getMaxMinSpanLength(structureUnits, ["C", "V"])?.maxLength + " ft",
            margin: [5, 3, 0, 0],
          },
          {
            width: "25%",
            text: "B.G.04 Minimum Span Length: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text:
              getMaxMinSpanLength(structureUnits, ["C", "V"])?.minLength + " ft",
            margin: [5, 3, 0, 0],
          },
        ],
      },
    ],
    styles: styles,
    defaultStyle: {
      fontSize: 10,
    },
  };
  if (structureUnits?.length) {
    for (let structureUnit of structureUnits) {
      docDefinition.content.push(
        {
          table: {
            headerRows: 0,
            widths: ["33%", "33%", "*"],
            body: [
              [
                {
                  text: "Structure Unit ID: " + structureUnit.strUnitLabel,
                  fillColor: "#ebebeb",
                  alignment: "left",
                  style: ["sectionHeader"],
                },
                {
                  text: `Span Type: ${configStore.getReferenceValue(
                    REFERENCE_TABLE.STRUCTURE_UNIT_TYPE,
                    structureUnit.legacyStructureType,
                  )}`,
                  fillColor: "#ebebeb",
                  alignment: "center",
                  style: ["sectionHeader"],
                },
                {
                  text: "Unit Key: " + structureUnit.strUnitKey,
                  fillColor: "#ebebeb",
                  alignment: "right",
                  style: ["sectionHeader"],
                },
              ],
            ],
          },
          layout: "noBorders",
          margin: [0, 3, 0, 0],
        },
        {
          table: {
            headerRows: 0,
            widths: ["100%"],
            body: [
              [
                {
                  text: "Span " + structureUnit.strUnitLabel + " Details",
                  fillColor: "#ebebeb",
                  alignment: "center",
                  style: ["sectionHeader"],
                },
              ],
            ],
          },
          layout: "noBorders",
          margin: [0, 3, 0, 0],
        },
        {
          columns: [
            {
              width: "30%",
              text: "SU.01 Unit Key: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: structureUnit.strUnitKey,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: "SU.02 Unit ID: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "*",
              text: structureUnit.strUnitLabel,
              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "50%",
              text: "SU.03 Structure Unit Description: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "*",
              text: structureUnit.strUnitDescription,
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "SU.04 Legacy Structure Type: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: configStore.getReferenceValue(REFERENCE_TABLE.STRUCTURE_TYPE, structureUnit?.legacyStructureType),
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: "SP.21 Span Length: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "*",
              text: structureUnit?.spanLength,
              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "SP.02 Main Span Set Indicator: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: configStore.getReferenceValue(
                REFERENCE_TABLE.MAIN_SPANSET_INDICATOR,
                structureUnit?.mainSpanSetIndicator,
              ),
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: "SP.22 Span Flared: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "*",
              text: inspectionStore.getReportCheckbox(structureUnit?.spanFlared),
              style: "customFontAwesome",
              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "B.SP.01 Span Config. Designation: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: structureUnit?.spanConfigDesignation,

              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: "SP.03 NSTM Group: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(
                REFERENCE_TABLE.MAIN_SPANSET_INDICATOR,
                structureUnit?.nstmGroupNumber,
              ),
              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "B.SP.03 Number of Beam Lines: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: structureUnit?.numberOfBeamLines,

              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: "SP.05 Type of Steel: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "*",
              text: steelTypeDisplayValue(structureUnit?.structureUnitSteelTypes?.map((s) => s.steelType)),
              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "B.SP.04 Span Material: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: configStore.getReferenceValue(REFERENCE_TABLE.SPAN_MATERIAL, structureUnit?.spanMaterial),

              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: "SP.24 Dept Material Type: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(REFERENCE_TABLE.MATERIAL_MAKEUP, structureUnit?.deptMaterialType),
              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "B.SP.05 Span Continuity: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: configStore.getReferenceValue(REFERENCE_TABLE.SPAN_CONTINUITY, structureUnit?.spanContinuity),

              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: "SP.25 Dept Physical Type: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(REFERENCE_TABLE.PHYSICAL_MAKEUP, structureUnit?.deptPhysicalType),
              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "B.SP.06 Span Type: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: configStore.getReferenceValue(REFERENCE_TABLE.SPAN_TYPE, structureUnit?.spanType),

              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: "SP.26 Dept Span Inter: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(REFERENCE_TABLE.SPAN_INTERACTION, structureUnit?.deptSpanInteraction),
              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "B.SP.07 Span Prot. System: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: configStore.getReferenceValue(
                REFERENCE_TABLE.SPAN_PROTECTION_SYSTEM,
                structureUnit?.spanProtectSystem,
              ),

              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: "SP.27 Dept Struc Config: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(
                REFERENCE_TABLE.STRUCTURAL_CONFIGURATION,
                structureUnit?.deptStrucConfig,
              ),
              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "SP.15 Culvert Floor Type: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: configStore.getReferenceValue(REFERENCE_TABLE.CULVERT_FLOOR_TYPE, structureUnit?.culvertFloorType),
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: "SP.18 Min Fill Height: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "*",
              text: structureUnit?.minFillHeight,
              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "SP.16 Culvert Opening Type: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: configStore.getReferenceValue(
                REFERENCE_TABLE.CULVERT_OPENING_TYPE,
                structureUnit?.culvertOpeningType,
              ),

              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: "SP.19 Max Fill Height: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "*",
              text: structureUnit?.maxFillHeight,
              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "SP.17 Culvert Length along Centerline: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: structureUnit.culvertLengthCenterline,

              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: "SP.20 Hydraulic Opening Eff. Width: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "*",
              text: structureUnit?.hydraulicOpeningEffWidth,
              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "55%",
              text: "",
              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          table: {
            headerRows: 0,
            widths: ["100%"],
            body: [
              [
                {
                  text: "Culvert Notes",
                  fillColor: "#ebebeb",
                  alignment: "center",
                  style: ["sectionHeader"],
                },
              ],
            ],
          },
          layout: "noBorders",
          margin: [0, 3, 0, 0],
        },
        {
          text: "Top Slab: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          text: structure?.structureUnitInspComments?.find(
            (element) =>
              element?.commentType == INSPECTION_COMMENTS.CULVERT_TOP_SLAB &&
              element.structureUnitId == structureUnit.structureUnitId,
          )?.notes,
        },
        {
          text: "Barrel: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          text: structure?.structureUnitInspComments?.find(
            (element) =>
              element?.commentType == INSPECTION_COMMENTS.CULVERT_BARREL &&
              element.structureUnitId == structureUnit.structureUnitId,
          )?.notes,
        },
        {
          text: "Floor/Paving: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          text: structure?.structureUnitInspComments?.find(
            (element) =>
              element?.commentType == INSPECTION_COMMENTS.CULVERT_FLOOR_PAVE &&
              element.structureUnitId == structureUnit.structureUnitId,
          )?.notes,
        },
        {
          text: "Headwall: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          text: structure?.structureUnitInspComments?.find(
            (element) =>
              element?.commentType == INSPECTION_COMMENTS.CULVERT_HEADWALL &&
              element.structureUnitId == structureUnit.structureUnitId,
          )?.notes,
        },
        {
          text: "Wings: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          text: structure?.structureUnitInspComments?.find(
            (element) =>
              element?.commentType == INSPECTION_COMMENTS.CULVERT_WINGS &&
              element.structureUnitId == structureUnit.structureUnitId,
          )?.notes,
        },
        {
          text: "Settlement: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          text: structure?.structureUnitInspComments?.find(
            (element) =>
              element?.commentType == INSPECTION_COMMENTS.CULVERT_SETTLEMENT &&
              element.structureUnitId == structureUnit.structureUnitId,
          )?.notes,
        },
        {
          text: "Debris: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          text: structure?.structureUnitInspComments?.find(
            (element) =>
              element?.commentType == INSPECTION_COMMENTS.CULVERT_DEBRIS &&
              element.structureUnitId == structureUnit.structureUnitId,
          )?.notes,
        },
        {
          text: "Footing: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          text: structure?.structureUnitInspComments?.find(
            (element) =>
              element?.commentType == INSPECTION_COMMENTS.CULVERT_FOOTING &&
              element.structureUnitId == structureUnit.structureUnitId,
          )?.notes,
        },
      );
    }
    for (let structureUnit of waterWayUnits) {
      let underwaterInsp = structure?.inspectionsUnderwater?.find(
        (inspectionUnderwater) => inspectionUnderwater.strUnitKey == structureUnit.strUnitKey,
      );
      docDefinition.content.push(
        {
          table: {
            headerRows: 0,
            widths: ["100%"],
            body: [
              [
                {
                  text: structureUnit.strUnitLabel == "CIN" ? "Culvert Inlet - Waterway" : "Culvert Outlet - Waterway",
                  fillColor: "#ebebeb",
                  alignment: "center",
                  style: ["sectionHeader"],
                },
              ],
            ],
          },
          layout: "noBorders",
          margin: [0, 3, 0, 0],
        },
        {
          columns: [
            {
              width: "25%",
              text: "WA.27 Sub Unit: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "*",
              text: inspectionStore.getSubUnit(underwaterInsp?.strUnitKey),
              margin: [0, 5, 0, 0],
            },
            {
              width: "30%",
              text: "WA.40 Pier/Abutment Type: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "*",
              text: pierAndAbutTypes?.find((type) => {
                return type?.value == underwaterInsp?.subUnitType;
              })?.title,
              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "25%",
              text: "WA.28 Current Inspection? ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: inspectionStore.getReportCheckbox(underwaterInsp?.previousCurrent),
              style: "customFontAwesome",
              margin: [0, 3, 0, 0],
            },
            {
              width: "30%",
              text: "WA.41 Inv Foundation Type: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(REFERENCE_TABLE.PA_FOUNDATION_TYPE, underwaterInsp?.invFoundType),
              margin: [0, 3, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "25%",
              text: "WA.29 SCBI Code: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: underwaterInsp?.scbiCode,
              margin: [0, 3, 0, 0],
            },
            {
              width: "30%",
              text: "WA.42 OSA Foundation Type: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(REFERENCE_TABLE.OSA_FOUNDATION_TYPE, underwaterInsp?.foundType),
              margin: [0, 3, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "25%",
              text: "WA.30 SCBI Case: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: underwaterInsp?.scbiCase,
              margin: [0, 3, 0, 0],
            },
            {
              width: "30%",
              text: "WA.43 Streambed Material: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(
                REFERENCE_TABLE.STREAM_BED_MATERIAL,
                underwaterInsp?.streamBedMaterial,
              ),
              margin: [0, 3, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "25%",
              text: "WA.31 Scour Rating: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(
                REFERENCE_TABLE.OBSERVED_SCOUR_RATING,
                underwaterInsp?.obsScourRating,
              ),
              margin: [0, 3, 0, 0],
            },
            {
              width: "30%",
              text: "WA.44 UW Inspection Type: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(
                REFERENCE_TABLE.UNDERWATER_INSPECTION_TYPE,
                underwaterInsp?.underwaterInspType,
              ),
              margin: [0, 3, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "25%",
              text: "WA.32 Change Since Last Insp: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(
                REFERENCE_TABLE.CHANGE_SINCE_LAST_INSP,
                underwaterInsp?.changeSinceLastInsp,
              ),
              margin: [0, 3, 0, 0],
            },
            {
              width: "30%",
              text: "WA.45 Observed Scour Depth: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: underwaterInsp?.obsScourDepth,
              margin: [0, 3, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "25%",
              text: "WA.33 Scour Hole: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(REFERENCE_TABLE.SCOUR_HOLE, underwaterInsp?.scourHole),
              margin: [0, 3, 0, 0],
            },
            {
              width: "30%",
              text: "WA.46 Water Depth: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: underwaterInsp?.maxWaterDepth,
              margin: [0, 3, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "25%",
              text: "WA.34 Debris Potential: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(REFERENCE_TABLE.DEBRIS_POTENTIAL, underwaterInsp?.debrisPotential),
              margin: [0, 3, 0, 0],
            },
            {
              width: "30%",
              text: "WA.47 Movement Indicator: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: inspectionStore.getReportCheckbox(underwaterInsp?.movement),
              style: "customFontAwesome",
              margin: [0, 3, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "25%",
              text: "WA.35 Scourability: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(REFERENCE_TABLE.SUBSTRUCTURE_SCOURABILITY, underwaterInsp?.subScour),
              margin: [0, 3, 0, 0],
            },
            {
              width: "30%",
              text: "WA.48 Scour/Undermine Indicator: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: inspectionStore.getReportCheckbox(underwaterInsp?.scourUndermine),
              style: "customFontAwesome",
              margin: [0, 3, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "25%",
              text: "WA.36 Opening Adeq Channel: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(
                REFERENCE_TABLE.OPENING_ADEQUACY_CHANNEL,
                underwaterInsp?.openAdeqChannel,
              ),
              margin: [0, 3, 0, 0],
            },
            {
              width: "30%",
              text: "WA.49 Countermeasures: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: inspectionStore.getReportCheckbox(underwaterInsp?.counterMeasure),
              style: "customFontAwesome",
              margin: [0, 3, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "25%",
              text: "WA.37 Sediment: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(REFERENCE_TABLE.SEDIMENT_DEPOSITS, underwaterInsp?.sedDeposit),
              margin: [0, 3, 0, 0],
            },
            {
              width: "30%",
              text: "WA.50 100yr Flood Scour Depth: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: underwaterInsp?.calcScourDepth100,
              margin: [0, 3, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "25%",
              text: "WA.38 Alignment: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(REFERENCE_TABLE.ALIGNMENT, underwaterInsp?.alignment),
              margin: [0, 3, 0, 0],
            },
            {
              width: "30%",
              text: "WA.51 500yr Flood Scour Depth: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: underwaterInsp?.calcScourDepth500,
              margin: [0, 3, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "25%",
              text: "WA.39 Velocity Stream Slope: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: configStore.getReferenceValue(
                REFERENCE_TABLE.VELOCITY_STREAM_SLOPE,
                underwaterInsp?.veloStreamSlope,
              ),
              margin: [0, 3, 0, 0],
            },
            {
              width: "30%",
              text: "WA.52 In 500yr Flood Plain? ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
            {
              width: "*",
              text: inspectionStore.getReportCheckbox(
                isStructureUnitIn500YrFp(structureUnit.strUnitKey, structure.inspectionsUnderwater) ? "1" : "0",
              ),
              style: "customFontAwesome",
              margin: [0, 3, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "25%",
              text: "WA.53 Notes: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "*",
              text: underwaterInsp?.underwaterInspDesc,
            },
          ],
        },
      );
    }
  } else {
    docDefinition.content.push({
      text: "This structure currently contains no structure unit details.",
      bold: true,
      alignment: "center",
      margin: [0, 10, 0, 0],
    });
  }
  return docDefinition;
}

export { setCulvertDocDefinition };
